import React, { useState, useEffect } from 'react';
import { MainLayout } from '../../components/layout/MainLayout';
import { cuttingOrderService } from '../../api/services/cuttingOrderService';
import {
  CuttingOrdersResponse,
  CuttingOrderGroup,
  CuttingOrder,
  CuttingActionType
} from '../../../../shared/types';
import toast from 'react-hot-toast';
import { Loader2, X, ChevronDown, ChevronRight, Printer, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { useAuthStore } from '../../store/authStore';

const CuttingStation: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const [loading, setLoading] = useState(true);
  const [ordersData, setOrdersData] = useState<CuttingOrdersResponse | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [orderDetail, setOrderDetail] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showPrintConfirm, setShowPrintConfirm] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  // 6 = Machine Cut, 11 = Pull From Stock
  const [selectedPrintType, setSelectedPrintType] = useState<6 | 11>(6);
  const [processingAction, setProcessingAction] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [braOtherExpanded, setBraOtherExpanded] = useState(false);
  const [selectedPartType, setSelectedPartType] = useState<'All' | 'Seat Cover' | 'Dash Cover' | 'Cargo Liner'>('All');
  const [showReprintModal, setShowReprintModal] = useState(false);
  const [reprintOrderId, setReprintOrderId] = useState('');
  const [loadingReprint, setLoadingReprint] = useState(false);

  // Map status IDs to names
  const getStatusName = (statusId: number, actionName?: string): string => {
    const statusMap: { [key: number]: string } = {
      1: 'UNCUT',
      6: 'CUT',
      10: 'HAND CUT',
      11: 'PULLED FROM STOCK'
    };
    // Re-printed orders can be past cutting (e.g. In Sewing), so fall back to the DB action name
    return statusMap[statusId] || actionName?.toUpperCase() || 'UNKNOWN';
  };

  useEffect(() => {
    loadCuttingOrders();
  }, []);

  const loadCuttingOrders = async () => {
    try {
      setLoading(true);
      const data = await cuttingOrderService.getCuttingOrders();
      setOrdersData(data);
    } catch (error) {
      console.error('Error loading cutting orders:', error);
      toast.error('Failed to load cutting orders');
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = async (order: CuttingOrder) => {
    try {
      setSelectedOrderId(order.OrderNumber);
      setShowModal(true);
      setLoadingDetail(true);
      const detail = await cuttingOrderService.getCuttingOrderDetail(order.OrderNumber);
      setOrderDetail(detail);
    } catch (error) {
      console.error('Error loading order detail:', error);
      toast.error('Failed to load order detail');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Re-print Order: look up any sales order (even one no longer in the cutting queue)
  // and open it in the order detail modal, where already-cut items can be re-printed
  const handleReprintOrderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const orderId = parseInt(reprintOrderId.trim(), 10);
    if (isNaN(orderId)) {
      toast.error('Enter a valid sales order number');
      return;
    }

    try {
      setLoadingReprint(true);
      const detail = await cuttingOrderService.getCuttingOrderDetail(orderId);
      if (!detail) {
        toast.error(`Sales order ${orderId} not found`);
        return;
      }
      setShowReprintModal(false);
      setReprintOrderId('');
      setSelectedOrderId(orderId);
      setOrderDetail(detail);
      setShowModal(true);
    } catch (error) {
      // API client already toasts the server's error message (e.g. 404 not found)
      console.error('Error loading order for re-print:', error);
    } finally {
      setLoadingReprint(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedOrderId(null);
    setOrderDetail(null);
  };

  const handlePrintClick = (itemId: number, type: 6 | 11) => {
    setSelectedItemId(itemId);
    setSelectedPrintType(type);
    setShowPrintConfirm(true);
  };

  // Re-print only reprints the traveler for the item's current cut state; it doesn't change state
  const handleReprint = async (itemId: number) => {
    try {
      const printData = await cuttingOrderService.getCuttingPrintData(itemId);
      printTraveler(printData);
    } catch (error) {
      console.error('Error re-printing traveler:', error);
      toast.error('Failed to load traveler');
    }
  };

  const handleConfirmPrint = async () => {
    if (!selectedItemId) return;

    try {
      setProcessingAction(true);
      // Action type 6 = Item Cut (Machine Cut), 11 = Pulled From Stock (also removes one unit from Stock)
      const result: any = await cuttingOrderService.performAction(
        selectedItemId,
        selectedPrintType,
        user?.UserName || 'system'
      );
      const binNumber = result?.binNumber ?? result?.data?.binNumber;
      toast.success(selectedPrintType === 11
        ? `Order item pulled from stock${binNumber ? ` (Bin #${binNumber})` : ''}`
        : 'Order item moved to Machine Cut state');

      // Get print data for the traveler
      const printData = await cuttingOrderService.getCuttingPrintData(
        selectedItemId,
        selectedPrintType,
        binNumber ? String(binNumber) : undefined
      );

      // Open print window with traveler
      printTraveler(printData);

      // Reload the order detail to show updated status
      if (selectedOrderId) {
        const detail = await cuttingOrderService.getCuttingOrderDetail(selectedOrderId);
        setOrderDetail(detail);
      }

      // Reload the orders list
      await loadCuttingOrders();
    } catch (error: any) {
      console.error('Error performing action:', error);
      toast.error(error?.response?.data?.message || 'Failed to update order item');
      // State may have changed under us (e.g. already cut / stock gone) — refresh
      if (selectedOrderId) {
        cuttingOrderService.getCuttingOrderDetail(selectedOrderId).then(setOrderDetail).catch(() => undefined);
      }
    } finally {
      setProcessingAction(false);
      setShowPrintConfirm(false);
      setSelectedItemId(null);
    }
  };

  const printTraveler = (response: any) => {
    // The API returns { success: true, data: {...} }
    const data = response?.data || response;

    if (!data) {
      toast.error('No print data available');
      return;
    }

    // Traveler variant, matching legacy CuttingPrint.cshtml
    const isStock = Number(data.Type) === 11;
    const isHandCut = Number(data.Type) === 10;
    const cutSection = isStock
      ? `<div class="section-subtitle">* PULL FROM STOCK *</div>
                <div class="checklist" style="font-size: 11pt; font-weight: bold;">Bin #: ${data.BinNumber ?? ''}</div>`
      : `<div class="section-subtitle">${isHandCut ? '* HAND CUT *' : 'MACHINE CUT'}</div>
                <div class="checklist">Product Cut to Workmanship Criteria</div>
                <div class="checklist">Product Self-Inspected to Workmanship Criteria</div>`;
    const sewnSection = isStock
      ? `<div class="section-subtitle">* PULL FROM STOCK *</div>`
      : `<div class="checklist">Product Sewn to Workmanship Criteria</div>
                <div class="checklist">Product Self-inspected to Workmanship Criteria</div>`;

    const printWindow = window.open('', '_blank', 'width=800,height=1000');
    if (!printWindow) {
      toast.error('Please allow popups to print the traveler');
      return;
    }

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Order Traveler - ${data.ItemNumber}</title>
        <style>
          @media print {
            @page {
              margin: 0.3in 0.4in;
              size: letter portrait;
            }
            body { margin: 0; }
          }
          * {
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 10px;
            font-size: 10pt;
            line-height: 1.2;
          }
          .header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
            font-size: 9pt;
          }
          .header-left {
            flex: 1;
          }
          .header-left div {
            margin-bottom: 2px;
          }
          .header-right {
            text-align: right;
            font-size: 20pt;
            font-weight: bold;
            line-height: 1;
          }
          .header-barcode {
            margin-top: 6px;
            text-align: right;
          }
          .item-number {
            font-size: 28pt;
            font-weight: bold;
            margin: 8px 0;
            line-height: 1;
          }
          .part-info {
            margin: 5px 0;
            font-size: 20px;
          }
          .barcode {
            text-align: right;
            margin: 5px 0;
            min-height: 20px;
          }
          .section-headers {
            display: flex;
            justify-content: space-between;
            margin: 28px 0 8px 0;
            font-weight: bold;
            font-size: 11pt;
          }
          .section {
            border-top: 1px solid #000;
            padding: 16px 0;
            page-break-inside: avoid;
          }
          .section-title {
            font-size: 11pt;
            font-weight: bold;
          }
          .section-subtitle {
            font-weight: bold;
            margin-bottom: 3px;
            font-size: 9pt;
          }
          .checklist {
            margin: 3px 0;
            font-size: 8pt;
            line-height: 1.3;
          }
          .checkbox {
            display: inline-block;
            width: 12px;
            height: 12px;
            border: 1.5px solid #000;
            margin-right: 5px;
            vertical-align: middle;
          }
          .signature-line {
            border-bottom: 1.5px solid #000;
            margin: 16px 0 4px 0;
            padding-bottom: 52px;
          }
          .signature-label {
            text-align: center;
            font-size: 8pt;
          }
          .datetime {
            text-align: right;
            font-weight: bold;
            font-size: 9pt;
          }
          .footer {
            text-align: right;
            margin-top: 10px;
            font-size: 8pt;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          td {
            vertical-align: top;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left">
            <div><strong>Order Date:</strong> ${new Date(data.OrderDate).toLocaleDateString()}</div>
            <div><strong>ESD:</strong> ${data.EstShipDate ? new Date(data.EstShipDate).toLocaleDateString() : 'N/A'}</div>
            <div style="margin-top: 10px;"><strong>Sales Order Number:</strong> ${data.OrderId}</div>
            <div><strong>Customer:</strong> ${data.CustomerId}</div>
          </div>
          <div class="header-right">
            ${data.ActionCode || ''}
            <div class="header-barcode">
              <img src="${data.BarcodeImage}" style="max-width: 144px; display: block; margin-left: auto;" />
            </div>
          </div>
        </div>

        <div class="item-number">${data.ItemNumber}</div>

        <div class="part-info"><strong>Part Number:</strong> ${data.PartNumber}</div>
        <div class="part-info">${data.Description || ''}</div>

        <div class="section-headers">
          <div>Operator</div>
          <div>Date & Time</div>
        </div>

        <div class="section">
          <table style="width: 100%;">
            <tr>
              <td style="width: 20%; vertical-align: top;">
                <div class="section-title">CUT</div>
              </td>
              <td style="width: 60%;">
                ${cutSection}
                <div class="signature-line"></div>
                <div class="signature-label">Operator's Signature</div>
              </td>
              <td style="width: 20%; text-align: right; vertical-align: top;">
                <div class="datetime">${new Date().toLocaleString()}</div>
              </td>
            </tr>
          </table>
        </div>

        <div class="section">
          <table style="width: 100%;">
            <tr>
              <td style="width: 20%; vertical-align: top;">
                <div class="section-title">SEWN</div>
              </td>
              <td style="width: 60%;">
                ${sewnSection}
                <div class="signature-line"></div>
                <div class="signature-label">Operator's Signature</div>
              </td>
              <td style="width: 20%; text-align: right;"></td>
            </tr>
          </table>
        </div>

        <div class="section">
          <table style="width: 100%;">
            <tr>
              <td style="width: 20%; vertical-align: top;">
                <div class="section-title">TRIM / PACKED / QC</div>
              </td>
              <td style="width: 80%;">
                <div class="checklist">Product Trimmed to Workmanship Criteria</div>
                <div class="checklist">Product Packed to CalTrend Specifications</div>
                <div class="checklist">Product Part Number verified & affixed to Box</div>
                <div class="checklist"><span class="checkbox"></span>Product Passed Final Inspection</div>
                <div class="signature-line"></div>
                <div class="signature-label">Operator's Signature</div>
              </td>
            </tr>
          </table>
        </div>

        <div class="section">
          <table style="width: 100%;">
            <tr>
              <td style="width: 20%; vertical-align: top;">
                <div class="section-title">SHIPPED</div>
              </td>
              <td style="width: 80%;">
                <div class="checklist">Product Verified Against Sales Order</div>
                <div class="checklist">
                  <span class="checkbox"></span>Shipped To Customer
                  &nbsp;&nbsp;&nbsp;&nbsp;
                  <span class="checkbox"></span>To Stock
                </div>
                <div class="signature-line"></div>
                <div class="signature-label">Operator's Signature</div>
              </td>
            </tr>
          </table>
        </div>

        <div class="footer">PD Form 3000</div>

        <script></script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleCancelPrint = () => {
    setShowPrintConfirm(false);
    setSelectedItemId(null);
  };

  // Get all unique group keys for the filter dropdown
  const groupKeys = ordersData?.orders.map(g => g.GroupKey) || [];

  // Calculate total items and unique orders
  const totalItems = ordersData?.count || 0;
  const totalOrders = ordersData?.orderCount || 0;

  // Counts per part type, across all orders regardless of the other active filters
  const partTypeCounts = { 'Seat Cover': 0, 'Dash Cover': 0, 'Cargo Liner': 0, 'Other': 0 };
  ordersData?.orders.forEach(group => {
    group.Orders.forEach(order => {
      const type = order.Item.PartType || 'Other';
      partTypeCounts[type] = (partTypeCounts[type] || 0) + 1;
    });
  });

  // Filter orders based on selected part type, date, and part number group.
  // Part type and date are applied per order-item (a group can contain a mix of
  // part types, e.g. "BRA/OTHER" holds Dash Cover, Cargo Liner and misc items together).
  const filteredOrders = ordersData?.orders
    .map(group => {
      const filteredGroupOrders = group.Orders.filter(order => {
        const matchesPartType = selectedPartType === 'All' || order.Item.PartType === selectedPartType;

        const matchesDateFilter = !selectedDate || (
          order.OrderDate && format(new Date(order.OrderDate), 'yyyy-MM-dd') === selectedDate
        );

        return matchesPartType && matchesDateFilter;
      });

      return { ...group, Orders: filteredGroupOrders };
    })
    .filter(group => {
      const matchesPartFilter = selectedFilter === 'all' || group.GroupKey === selectedFilter;
      return matchesPartFilter && group.Orders.length > 0;
    }) || [];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
        </div>
      </MainLayout>
    );
  }

  const hasOrders = (ordersData?.orders.length ?? 0) > 0;

  return (
    <MainLayout>
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">CalTrend Cutting Station</h2>

              {/* Filters */}
              <div className="flex items-center gap-4">
                {/* Date Filter */}
                <div className="flex items-center gap-2">
                  <label htmlFor="date-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Filter by Date:
                  </label>
                  <input
                    type="date"
                    id="date-filter"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                  />
                  {selectedDate && (
                    <button
                      onClick={() => setSelectedDate('')}
                      className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                      title="Clear date filter"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* Part Filter */}
                <div className="flex items-center gap-2">
                  <label htmlFor="group-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Filter by Part:
                  </label>
                  <select
                    id="group-filter"
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                  >
                    <option value="all">All Parts ({ordersData?.orders.length ?? 0})</option>
                    {groupKeys.map((key) => (
                      <option key={key} value={key}>
                        {key}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={() => setShowReprintModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 text-sm font-medium whitespace-nowrap"
                >
                  <Printer className="w-4 h-4" />
                  Re-print Order
                </button>
                <button
                  onClick={loadCuttingOrders}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 text-sm font-medium"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
              </div>
            </div>

            {/* Part Type Filter Buttons */}
            <div className="flex items-center gap-2 mb-3">
              {(['All', 'Seat Cover', 'Dash Cover', 'Cargo Liner'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedPartType(type)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    selectedPartType === type
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  {type}
                  {type !== 'All' && (
                    <span className="ml-1 opacity-80">({partTypeCounts[type]})</span>
                  )}
                </button>
              ))}
            </div>

            {/* Summary */}
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-semibold">Uncut items: {totalItems}</span> in <span className="font-semibold">{totalOrders} orders</span>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {filteredOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                {hasOrders ? 'No orders found for the selected filter.' : 'No orders in cutting queue.'}
              </div>
            ) : (
              filteredOrders.map((group: CuttingOrderGroup) => {
                const isBraOther = group.GroupKey === 'BRA/OTHER';
                const isExpanded = isBraOther ? braOtherExpanded : true;

                return (
                  <div key={group.GroupKey} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                    <div
                      className={`bg-gray-50 dark:bg-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600 ${
                        isBraOther ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600' : ''
                      }`}
                      onClick={() => isBraOther && setBraOtherExpanded(!braOtherExpanded)}
                    >
                      <div className="flex items-center gap-2">
                        {isBraOther && (
                          braOtherExpanded ? (
                            <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                          )
                        )}
                        <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">{group.GroupKey}</h3>
                        {isBraOther && (
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            ({group.Orders.length} orders)
                          </span>
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {group.Orders.map((order: CuttingOrder) => (
                              <tr key={`${order.OrderNumber}-${order.Item.Ordinal}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                  {order.OrderDate && format(new Date(order.OrderDate), 'MM/dd/yy')}
                                </td>
                                <td className="px-4 py-3">
                                  <span className="bg-orange-100 dark:bg-orange-900 px-2 py-1 rounded font-semibold text-orange-800 dark:text-orange-200">
                                    {order.OrderNumber}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{order.CustomerId}</td>
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{order.Item.ActionCode || 'EA'}</td>
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{order.ItemCount}</td>
                                <td className="px-4 py-3">
                                  {(order.Item.StockCount ?? 0) > 0 && (
                                    <span className="px-2 py-1 rounded text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300">
                                      In Stock
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <button
                                    onClick={() => handleViewOrder(order)}
                                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100"
                                  >
                                    View Order
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Modal for Order Details */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 px-6 py-3 flex justify-between items-center sticky top-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">CalTrend Cutting Station</h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-700 dark:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {loadingDetail ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
                </div>
              ) : orderDetail ? (
                <div>
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Customer: {orderDetail.CustomerId}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{orderDetail.Items?.length || 0} items</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Sales Order #: {selectedOrderId}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Date: {orderDetail.OrderDate && format(new Date(orderDetail.OrderDate), 'MM/dd/yy')}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Entered by: icordero</p>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse border border-gray-300 dark:border-gray-600">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-700">
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Item No.</th>
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Part Number</th>
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Description</th>
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Status</th>
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800">
                        {orderDetail.Items?.map((item: any) => (
                          <tr key={item.Id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                              {selectedOrderId}-{item.Ordinal}-{orderDetail.TotalItemCount}
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {item.PartNumber}
                              {item.Status === 1 && item.StockCount > 0 && (
                                <div className="mt-1 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                                  In stock: {item.StockCount} · Bin #{item.StockBinNumber}
                                </div>
                              )}
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{item.Description}</td>
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                              <span className={`font-semibold text-sm ${
                                item.Status === 1 ? 'text-red-600 dark:text-red-400' :
                                item.Status === 6 || item.Status === 11 ? 'text-green-600 dark:text-green-400' :
                                'text-gray-600 dark:text-gray-400'
                              }`}>
                                {getStatusName(item.Status, item.StatusName)}
                              </span>
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                              <div className="flex gap-2">
                                {item.Status === 1 && item.StockCount > 0 ? (
                                  <>
                                    <button
                                      onClick={() => handlePrintClick(item.Id, 6)}
                                      className="px-3 py-1 bg-gray-200 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 text-sm whitespace-nowrap"
                                    >
                                      Print Traveler - Machine Cut
                                    </button>
                                    <button
                                      onClick={() => handlePrintClick(item.Id, 11)}
                                      className="px-3 py-1 bg-emerald-600 border border-emerald-600 rounded hover:bg-emerald-700 text-white text-sm whitespace-nowrap"
                                    >
                                      Print Traveler - Pull From Stock
                                    </button>
                                  </>
                                ) : item.Status === 1 ? (
                                  <button
                                    onClick={() => handlePrintClick(item.Id, 6)}
                                    className="px-3 py-1 bg-gray-200 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 text-sm"
                                  >
                                    Print
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleReprint(item.Id)}
                                    className="px-3 py-1 bg-gray-200 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 text-sm"
                                  >
                                    Re-print
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-center text-gray-600 dark:text-gray-400 py-12">No order details available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Re-print Order prompt (legacy "Pre-print order") */}
      {showReprintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <form
            onSubmit={handleReprintOrderSubmit}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full"
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Re-print Order</h3>
              <button
                type="button"
                onClick={() => { setShowReprintModal(false); setReprintOrderId(''); }}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-700 dark:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5">
              <label htmlFor="reprint-order-id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sales Order
              </label>
              <input
                id="reprint-order-id"
                type="text"
                inputMode="numeric"
                autoFocus
                value={reprintOrderId}
                onChange={(e) => setReprintOrderId(e.target.value)}
                placeholder="e.g. 1178867"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex justify-end rounded-b-lg">
              <button
                type="submit"
                disabled={loadingReprint || !reprintOrderId.trim()}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loadingReprint && <Loader2 className="w-4 h-4 animate-spin" />}
                Submit
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Print Confirmation Dialog */}
      {showPrintConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              awsatlas.caltrend.com says
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              {selectedPrintType === 11
                ? 'This will change order item to Pull from stock state and also remove item from stock data. Are you sure?'
                : 'This will change order item to Machine Cut state. Are you sure?'}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleConfirmPrint}
                disabled={processingAction}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {processingAction ? 'Processing...' : 'OK'}
              </button>
              <button
                onClick={handleCancelPrint}
                disabled={processingAction}
                className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default CuttingStation;
